<template>
    <div class="automation-wizard-field-pipeline-step-opp">
        <CompassSkeleton
            v-if="showSkeleton"
            size="x-large"
            class="automation-wizard-field-pipeline-step-opp__skeleton"
        />
        <CompassFormItem v-else v-bind="formItemProps" :error="field.currentError || ''">
            <CompassSelect
                v-model="localValue"
                v-bind="selectProps"
                :loading="loading"
                :disabled="disabled"
                menuClass="automation-wizard-pipeline-step-select"
            >
                <CompassDropdownSelectOption
                    v-for="option in options"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                />
            </CompassSelect>
        </CompassFormItem>
    </div>
</template>

<script>
import { getPipelines, getPipelineSteps } from 'api/automation/automationService.js';

// Global cache to share data between instances (and avoid redundant API calls)
const globalCache = {
    pipelines: null,
    steps: new Map(),
    loadingPipelines: false,
    loadingSteps: new Set(),
};

export default {
    props: {
        field: {
            type: Object,
            required: true,
        },
        value: {
            type: [String, Number, Object],
            default: '',
        },
        formData: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['update:value'],
    data() {
        return {
            options: [],
            loading: false,
            localValue: this.value || '',
            currentPipelineId: null,
        };
    },
    computed: {
        formItemProps() {
            return {
                label: this.field.props?.formItem?.label || '',
                required: this.field.required || false,
                requiredLabel:
                    this.field.props?.formItem?.requiredLabel ||
                    this.$t.__('common.labels.required'),
                ...this.field.props?.formItem,
            };
        },
        selectProps() {
            return {
                ...this.field.props?.select,
            };
        },
        isStepField() {
            return this.field.id?.includes('stepOpp') || false;
        },
        selectedPipelineId() {
            if (!this.isStepField) {
                return null;
            }
            // stepOpp is always linked to pipelineOpp
            return this.formData.pipelineOpp || null;
        },
        disabled() {
            return this.field.disabled || (this.isStepField && !this.selectedPipelineId);
        },
        showSkeleton() {
            return this.loading && this.options.length === 0;
        },
    },
    watch: {
        value: {
            async handler(newValue) {
                this.localValue = newValue || '';
                // If we have a value but no options, we need to load data
                if (newValue && this.options.length === 0) {
                    if (this.isStepField) {
                        const pipelineId = this.selectedPipelineId;
                        if (pipelineId) {
                            await this.loadSteps(pipelineId);
                        }
                    } else {
                        await this.loadPipelines();
                    }
                }
            },
            immediate: true,
        },
        localValue(newValue) {
            this.$emit('update:value', newValue);
        },
        selectedPipelineId: {
            async handler(newPipelineId, oldPipelineId) {
                if (!this.isStepField || newPipelineId === this.currentPipelineId) {
                    return;
                }
                // Reset stepOpp value if pipeline changes (and we had a previous value)
                if (oldPipelineId && oldPipelineId !== newPipelineId && this.localValue) {
                    this.localValue = '';
                }
                // Load steps of the new pipeline
                this.currentPipelineId = newPipelineId;
                await this.loadSteps(newPipelineId);
            },
            immediate: true,
        },
        formData: {
            handler() {
                // For step fields, reload options if the pipeline has changed
                if (this.isStepField) {
                    this.$nextTick(() => {
                        const pipelineId = this.selectedPipelineId;
                        if (pipelineId !== this.currentPipelineId) {
                            this.currentPipelineId = pipelineId;
                            this.loadSteps(pipelineId);
                        }
                    });
                }
            },
            deep: true,
        },
    },
    async mounted() {
        await this.loadInitialOptions();
    },
    methods: {
        autoSelectSingleOption() {
            if (this.options.length === 1 && !this.localValue) {
                this.localValue = this.options[0].value;
            }
        },
        async loadInitialOptions() {
            if (this.isStepField) {
                if (this.selectedPipelineId) {
                    await this.loadSteps(this.selectedPipelineId);
                }
            } else {
                await this.loadPipelines();
            }
        },
        async loadPipelines() {
            if (globalCache.pipelines) {
                this.options = globalCache.pipelines;
                this.$nextTick(() => {
                    this.autoSelectSingleOption();
                });
                return;
            }
            if (globalCache.loadingPipelines) {
                return;
            }
            try {
                this.loading = true;
                globalCache.loadingPipelines = true;
                const response = await getPipelines();
                const pipelines = response.data || [];
                const options = pipelines.map((pipeline) => ({
                    value: pipeline.id,
                    label: pipeline.name,
                }));
                globalCache.pipelines = options;
                this.options = options;
                this.$nextTick(() => {
                    this.autoSelectSingleOption();
                });
            } catch (error) {
                this.options = [];
                throw error;
            } finally {
                this.loading = false;
                globalCache.loadingPipelines = false;
            }
        },
        async loadSteps(pipelineId) {
            if (!pipelineId) {
                this.options = [];
                return;
            }
            if (globalCache.steps.has(pipelineId)) {
                this.options = globalCache.steps.get(pipelineId);
                this.$nextTick(() => {
                    this.autoSelectSingleOption();
                });
                return;
            }
            if (globalCache.loadingSteps.has(pipelineId)) {
                return;
            }
            try {
                this.loading = true;
                globalCache.loadingSteps.add(pipelineId);
                const response = await getPipelineSteps(pipelineId);
                const steps = response.data || [];
                const options = steps.map((step) => ({
                    value: step.id,
                    label: step.name,
                }));
                globalCache.steps.set(pipelineId, options);
                this.options = options;
                this.$nextTick(() => {
                    this.autoSelectSingleOption();
                });
            } catch (error) {
                this.options = [];
                throw error;
            } finally {
                this.loading = false;
                globalCache.loadingSteps.delete(pipelineId);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-wizard-field-pipeline-step-opp {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    &__skeleton {
        width: 100%;
        ::v-deep .compass-skeleton__line--x-large {
            height: 56px;
        }
    }
}
</style>

<style lang="scss">
#compass-dropdown-container {
    .automation-wizard-pipeline-step-select {
        z-index: 1001;
    }
}
</style>
